import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Code, CheckCircle, AlertTriangle, Eye, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSS_SNIPPETS = [
  {
    name: 'Rounded Cards',
    css: `/* Rounded card style */\n.card, [class*="Card"] {\n  border-radius: 16px !important;\n}`
  },
  {
    name: 'Custom Accent Color',
    css: `/* Custom accent */\n.bg-blue-600 { background-color: #7c3aed !important; }\n.text-blue-600 { color: #7c3aed !important; }`
  },
  {
    name: 'Hide Sidebar Groups',
    css: `/* Compact sidebar */\n[data-testid^="nav-group-"] {\n  font-size: 10px;\n}`
  }
];

export default function TenantCustomCSS() {
  const { t } = useI18n();
  const [css, setCss] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchCSS();
  }, []);

  const fetchCSS = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/custom-css/tenant`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCss(response.data.css || '');
      setEnabled(response.data.enabled || false);
    } catch (error) {
      // First time - no CSS yet
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/custom-css/validate`, { css }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setValidation(response.data);
      if (response.data.valid) {
        toast.success('CSS is valid');
      } else {
        toast.error('CSS has issues');
      }
    } catch (error) {
      toast.error('Validation failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/custom-css/tenant`, { css, enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Custom CSS saved');
      setValidation(null);

      // Apply or remove preview
      if (enabled) {
        applyPreview(css);
      } else {
        removePreview();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save CSS');
    } finally {
      setSaving(false);
    }
  };

  const applyPreview = (cssText) => {
    let styleEl = document.getElementById('tenant-custom-css-preview');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'tenant-custom-css-preview';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = cssText;
    setPreviewing(true);
  };

  const removePreview = () => {
    const styleEl = document.getElementById('tenant-custom-css-preview');
    if (styleEl) styleEl.remove();
    setPreviewing(false);
  };

  const insertSnippet = (snippet) => {
    setCss(prev => prev ? `${prev}\n\n${snippet.css}` : snippet.css);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Code className="h-6 w-6 text-violet-600" />
            Custom CSS
          </h1>
          <p className="text-slate-500 mt-1">
            Inject custom CSS for advanced visual customization
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="css-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="css-enabled-toggle"
            />
            <Label htmlFor="css-enabled" className="text-sm font-medium">
              {enabled ? 'Enabled' : 'Disabled'}
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => previewing ? removePreview() : applyPreview(css)}
            data-testid="css-preview-btn"
          >
            <Eye className="mr-2 h-4 w-4" />
            {previewing ? 'Remove Preview' : 'Preview'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="css-save-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save CSS'}
          </Button>
        </div>
      </div>

      {/* Validation Results */}
      {validation && (
        <Alert className={validation.valid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}>
          {validation.valid ? (
            <CheckCircle className="h-4 w-4 text-emerald-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription className={validation.valid ? 'text-emerald-700' : 'text-red-700'}>
            {validation.valid 
              ? `CSS is valid (${validation.size_bytes} bytes / ${validation.size_limit_bytes} max)` 
              : validation.errors.join(', ')}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Editor */}
        <div className="lg:col-span-3">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">CSS Editor</CardTitle>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleValidate}>
                    <CheckCircle className="mr-1 h-4 w-4" /> Validate
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setCss(''); removePreview(); }}>
                    <RotateCcw className="mr-1 h-4 w-4" /> Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <textarea
                value={css}
                onChange={(e) => setCss(e.target.value)}
                placeholder={`/* Enter your custom CSS here */\n\n/* Example: Change sidebar background */\n.bg-blue-50 {\n  background-color: #f0fdf4 !important;\n}`}
                className="w-full h-80 font-mono text-sm p-4 bg-slate-900 text-emerald-400 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y"
                spellCheck={false}
                data-testid="css-editor-textarea"
              />
              <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                <span>{css.length} characters</span>
                <span>Max: 50,000 characters</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Snippets Sidebar */}
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Snippets</CardTitle>
              <CardDescription className="text-xs">Click to insert</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {CSS_SNIPPETS.map((snippet, idx) => (
                <Button
                  key={`tena-${idx}`}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left text-xs"
                  onClick={() => insertSnippet(snippet)}
                  data-testid={`css-snippet-${idx}`}
                >
                  <Code className="mr-2 h-3 w-3 flex-shrink-0" />
                  {snippet.name}
                </Button>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-slate-600 space-y-2">
              <p>Use <code className="bg-slate-100 px-1 rounded">!important</code> to override defaults.</p>
              <p>Max size: 50KB.</p>
              <p>Blocked: <code className="bg-slate-100 px-1 rounded">@import</code>, JavaScript URLs, data URIs.</p>
              <p>Use Preview to test before saving.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
